import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import GoogleLogin from './GoogleLogin';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    activationCode: ''
  });
  const [showActivationCode, setShowActivationCode] = useState(false);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { register, error, clearError, isAuthenticated, loading, checkDomainRequiresActivation } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearError();
  }, [clearError]);

  useEffect(() => {
    // If user is already authenticated, redirect to home
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Add any API errors to our errors state
    if (error) {
      setErrors([error]);
    }
  }, [error]);

  const { name, email, password, confirmPassword, activationCode } = formData;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // If email is changed, check if domain requires activation
    if (name === 'email' && value.includes('@') && value.split('@')[1].includes('.')) {
      checkEmailDomain(value);
    }
  };
  
  const checkEmailDomain = async (email: string) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingDomain(true);
    try {
      const requiresActivation = await checkDomainRequiresActivation(email);
      setShowActivationCode(requiresActivation);
    } catch (err) {
      console.error('Error checking email domain:', err);
      setShowActivationCode(true); // Default to showing activation code field
    } finally {
      setCheckingDomain(false);
    }
  };

  const validateForm = () => {
    const validationErrors: string[] = [];
    
    if (!name) {
      validationErrors.push('Name is required');
    }
    
    if (!email) {
      validationErrors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      validationErrors.push('Email is invalid');
    }
    
    if (!password) {
      validationErrors.push('Password is required');
    } else if (password.length < 6) {
      validationErrors.push('Password must be at least 6 characters');
    }
    
    if (password !== confirmPassword) {
      validationErrors.push('Passwords do not match');
    }
    
    if (showActivationCode && !activationCode) {
      validationErrors.push('Activation code is required for this email domain');
    }
    
    return validationErrors;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors([]);
    
    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Check domain requirements one more time
    if (email.includes('@')) {
      await checkEmailDomain(email);
    }
    
    // Try to register
    const success = await register(
      name, 
      email, 
      password, 
      showActivationCode ? activationCode : undefined
    );
    
    if (success) {
      navigate('/');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-60px)] bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-md w-full bg-white p-10 rounded-xl shadow-lg border border-gray-100 transform transition duration-300 hover:shadow-xl">
        <h2 className="text-3xl font-semibold text-center mb-8 text-gray-800">Create an Account</h2>

        {errors.length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-md" role="alert">
            <ul className="list-disc pl-5">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label htmlFor="name" className="block text-gray-700 text-sm font-medium mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={onChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={onChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={onChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
              placeholder="Enter your password"
            />
            <p className="text-gray-500 text-xs mt-1 ml-1">Must be at least 6 characters</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={onChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
              placeholder="Confirm your password"
            />
          </div>
          
          {showActivationCode && (
            <div>
              <label htmlFor="activationCode" className="block text-gray-700 text-sm font-medium mb-2">
                Activation Code
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="activationCode"
                name="activationCode"
                value={activationCode}
                onChange={onChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                placeholder="Enter your activation code"
              />
              <p className="text-gray-500 text-xs mt-1 ml-1">Required for your email domain</p>
            </div>
          )}
          
          {checkingDomain && (
            <div className="text-blue-600 text-sm flex items-center mt-2">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking email domain...
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition duration-200 mt-6 ${
              loading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            ) : 'Sign Up'}
          </button>

          <div className="text-center text-gray-600 text-sm mt-4">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-800 transition duration-150">
              Sign in
            </Link>
          </div>

          <div className="relative py-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-white text-sm text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-2">
            <GoogleLogin
              onError={(errorMsg) => setErrors([errorMsg])}
              className="w-full"
              activationCode={showActivationCode ? activationCode : undefined}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;